import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/Select';
import { useOrganizations } from '@/state/useOrganizations';
import { useAuth } from 'hooks/useAuth';
import { useForm } from 'hooks/useForm';
import { COMPANY_SIZES } from 'definitions';
import { SlackNotifications } from '../Notifications';
import { LearningsContribution } from './LearningsContribution';
import { Panel, Field, Fact, FIELD_CONTROL } from '../../ConsolePanel';

type SettingsFormState = {
  friendlyName: string;
  size: string;
};

/**
 * The organization's own settings.
 *
 * Built from the shared primitives rather than a hand-rolled section, which is what
 * made this page read as off-design: it was a titled box with two inputs and a save
 * bar, with no description, no sense of which account it was editing, and its own
 * spelling of a panel.
 *
 * The two facts are shown because "which account am I changing" is the first question
 * a settings page has to answer, and an agency user switches between many. They are
 * read-only and look it - an account id is not a blank someone forgot to fill in.
 */
export function SettingsGeneral() {
  const { user } = useAuth();
  const { company } = user!;
  const { updateOrganization } = useOrganizations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { formRef, formState, update } = useForm<SettingsFormState>({
    friendlyName: company.friendlyName || '',
    size: company.size || '',
  });

  const { friendlyName, size } = formState;

  const isUnchanged = friendlyName === company.friendlyName && size === company.size;

  /** Same rule as everywhere else: a dead submit says why. */
  const blockingReason = !friendlyName
    ? 'Enter a company name'
    : !size
      ? 'Choose a company size'
      : isUnchanged
        ? 'No changes to save'
        : undefined;

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (blockingReason) return;

    setIsSubmitting(true);
    try {
      await updateOrganization(company.id, formState);
      toast.success('Organization settings saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sizeOptions = COMPANY_SIZES.map((option) => ({
    value: option,
    label: `${option} employees`,
  }));

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-[14px]">
      <form ref={formRef} onSubmit={onSubmit}>
        <Panel
          title="Organization"
          tourAnchor="settings-general-organization"
          description="How this account is named across Pertento, for everyone on the team."
          footer={
            <>
              {!!blockingReason && !isSubmitting && <span className="supporting sm:mr-auto">{blockingReason}</span>}
              <Button type="submit" className="h-9 rounded-lg text-body" disabled={isSubmitting || !!blockingReason}>
                {isSubmitting ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Company name" htmlFor="friendlyName" hint="Shown in the account picker and on invitations.">
                <Input
                  id="friendlyName"
                  name="friendlyName"
                  placeholder="Enter company name"
                  defaultValue={friendlyName}
                  className={FIELD_CONTROL}
                />
              </Field>

              <Field label="Company size">
                <Select
                  name="size"
                  options={sizeOptions}
                  update={update}
                  value={size}
                  placeholder="Select a size"
                  className={FIELD_CONTROL}
                />
              </Field>
            </div>

            <div className="border-line grid gap-4 border-t pt-4 sm:grid-cols-2">
              <Fact label="Account type" value={company.type} />
              <Fact label="Account id" value={company.id} />
            </div>
          </div>
        </Panel>
      </form>

      <LearningsContribution />

      <SlackNotifications />
    </div>
  );
}
